import { useEffect, useState } from "react";
import {
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  ClipboardCheck,
  Loader2,
  X,
  XCircle,
} from "lucide-react";
import {
  useRequirementsCoverage,
  type RequirementCoverage,
  type RequirementsCoverageResult,
} from "@/hooks/use-proposals";

/**
 * Mejora "B": diálogo de cobertura de requisitos del pliego.
 *
 * La evaluación por sección juzga la CALIDAD de redacción de cada sección en
 * aislamiento, y por eso marca falsos huecos (un requisito cubierto en otra
 * sección lo ve como "falta"). Este diálogo mira la propuesta COMPLETA contra
 * el pliego y muestra una matriz requisito → ¿cubierto? → ¿en qué sección(es)?.
 * Completitud a nivel propuesta, no a nivel sección.
 */

interface RequirementsCoverageDialogProps {
  isOpen: boolean;
  onClose: () => void;
  proposalId: string;
  proposalTitle: string;
}

const STATUS_STYLES = {
  cubierto: {
    bg: "bg-emerald-50",
    border: "border-emerald-200",
    color: "text-emerald-700",
    Icon: CheckCircle2,
    label: "Cubierto",
  },
  parcial: {
    bg: "bg-amber-50",
    border: "border-amber-200",
    color: "text-amber-800",
    Icon: AlertTriangle,
    label: "Parcial",
  },
  no_cubierto: {
    bg: "bg-red-50",
    border: "border-red-200",
    color: "text-red-700",
    Icon: XCircle,
    label: "No cubierto",
  },
} as const;

function requirementCard(r: RequirementCoverage, idx: number) {
  const s = STATUS_STYLES[r.status] ?? STATUS_STYLES.parcial;
  const sections = r.sections ?? [];
  return (
    <li
      key={`${idx}-${r.requirement.slice(0, 24)}`}
      className={`border ${s.border} ${s.bg} rounded-md p-3`}
    >
      <div className="flex items-start gap-2">
        <s.Icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${s.color}`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-gray-900">
              {r.requirement}
            </span>
            <span
              className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${s.bg} ${s.color} border ${s.border}`}
            >
              {s.label}
            </span>
          </div>
          {sections.length > 0 && (
            <p className="text-xs text-gray-600 mt-1">
              En:{" "}
              {sections.map((sec, i) => (
                <span key={i} className="font-medium text-gray-700">
                  {sec}
                  {i < sections.length - 1 ? ", " : ""}
                </span>
              ))}
            </p>
          )}
          {r.note && <p className="text-xs text-gray-700 mt-1">{r.note}</p>}
        </div>
      </div>
    </li>
  );
}

function SummaryBar({ result }: { result: RequirementsCoverageResult }) {
  const { summary } = result;
  const pct = Math.round((summary.coverage_score || 0) * 100);
  const barColor =
    pct >= 80 ? "bg-emerald-500" : pct >= 50 ? "bg-amber-500" : "bg-red-500";

  return (
    <div className="space-y-3">
      <div>
        <div className="flex items-center justify-between mb-1">
          <span className="text-sm font-medium text-gray-700">
            Cobertura del pliego
          </span>
          <span className="text-sm font-bold text-gray-900">{pct}%</span>
        </div>
        <div className="w-full h-2.5 bg-gray-100 rounded-full overflow-hidden">
          <div
            className={`h-full ${barColor} rounded-full transition-all`}
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-emerald-50 border border-emerald-200 rounded-md p-2 text-center">
          <div className="text-xl font-bold text-emerald-700">
            {summary.cubierto}
          </div>
          <div className="text-[11px] text-emerald-700">cubiertos</div>
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-md p-2 text-center">
          <div className="text-xl font-bold text-amber-700">
            {summary.parcial}
          </div>
          <div className="text-[11px] text-amber-700">parciales</div>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-md p-2 text-center">
          <div className="text-xl font-bold text-red-700">
            {summary.no_cubierto}
          </div>
          <div className="text-[11px] text-red-700">sin cubrir</div>
        </div>
      </div>
    </div>
  );
}

export function RequirementsCoverageDialog({
  isOpen,
  onClose,
  proposalId,
  proposalTitle,
}: RequirementsCoverageDialogProps) {
  const coverageMutation = useRequirementsCoverage(proposalId);
  const [lastResult, setLastResult] =
    useState<RequirementsCoverageResult | null>(null);

  // Lanzamos el análisis al abrir si aún no hay resultado. No lo metemos en
  // deps (useMutation devuelve objeto nuevo cada render) — mismo patrón que el
  // diálogo legal, para evitar bucles.
  const coverageMutate = coverageMutation.mutate;
  useEffect(() => {
    if (!isOpen) return;
    if (lastResult) return;
    coverageMutate(undefined, { onSuccess: (res) => setLastResult(res) });
  }, [isOpen, proposalId, lastResult, coverageMutate]);

  const handleRerun = () => {
    coverageMutation.mutate(undefined, {
      onSuccess: (res) => setLastResult(res),
    });
  };

  if (!isOpen) return null;

  const result = lastResult;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Cobertura de requisitos del pliego"
    >
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <ClipboardCheck className="w-5 h-5 text-blue-600" />
            <h2 className="text-lg font-semibold text-gray-900">
              Cobertura del pliego
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Cerrar"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          <p className="text-sm text-gray-600">
            Propuesta:{" "}
            <span className="font-medium text-gray-900">{proposalTitle}</span>
          </p>

          {coverageMutation.isPending && (
            <div className="flex items-center gap-2 p-4 bg-blue-50 border border-blue-200 rounded-md text-sm text-blue-800">
              <Loader2 className="w-4 h-4 animate-spin" />
              Comparando la propuesta completa con los requisitos del pliego…
            </div>
          )}

          {coverageMutation.isError && !result && (
            <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
              <AlertCircle className="w-4 h-4 mt-0.5" />
              <span>
                Error al analizar la cobertura. Pulsa <em>Reintentar</em> abajo.
              </span>
            </div>
          )}

          {result && result.notice && (
            <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-md text-sm text-amber-800">
              <AlertCircle className="w-4 h-4 mt-0.5" />
              <span>{result.notice}</span>
            </div>
          )}

          {result && !result.notice && (
            <>
              <SummaryBar result={result} />

              {result.summary.no_cubierto === 0 &&
              result.summary.parcial === 0 ? (
                <div className="flex items-center gap-2 p-3 bg-emerald-50 border border-emerald-200 rounded-md text-sm text-emerald-800">
                  <CheckCircle2 className="w-4 h-4" />
                  La propuesta cubre todos los requisitos clave detectados en el
                  pliego.
                </div>
              ) : (
                <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-md text-sm text-amber-800">
                  <AlertTriangle className="w-4 h-4 mt-0.5" />
                  <span>
                    Hay requisitos sin cubrir o cubiertos parcialmente. Revisa
                    los marcados abajo antes de presentar.
                  </span>
                </div>
              )}

              {(result.requirements ?? []).length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">
                    Requisitos analizados ({(result.requirements ?? []).length})
                  </h3>
                  <ul className="space-y-2">
                    {(result.requirements ?? []).map((r, idx) =>
                      requirementCard(r, idx)
                    )}
                  </ul>
                </div>
              )}

              <p className="text-[11px] text-gray-400">
                Los requisitos se extraen automáticamente del pliego con IA;
                úsalo como ayuda, no como checklist definitivo.
              </p>
            </>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-gray-200">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
          >
            Cerrar
          </button>
          <button
            type="button"
            onClick={handleRerun}
            disabled={coverageMutation.isPending}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {coverageMutation.isPending ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Analizando…
              </>
            ) : coverageMutation.isError && !lastResult ? (
              <>
                <ClipboardCheck className="w-4 h-4" />
                Reintentar
              </>
            ) : (
              <>
                <ClipboardCheck className="w-4 h-4" />
                Reanalizar
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default RequirementsCoverageDialog;
